/**
 * OwnerRegister — self-service signup for club / venue / school owners.
 *  - Collects name, email, password, owner type, and organization name.
 *  - Creates the user via auth.register, then auto-provisions a starter
 *    Club or Venue entity (so the dashboard isn't empty on first login).
 *  - On success, drops the owner straight into /dashboard.
 *  - Players signing up should use /register instead — link provided below.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, ArrowLeft, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { clubsAPI, venuesAPI } from '../lib/api';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3ec18717-9700-48f1-a5a2-71ac0edd3a6f/artifacts/29waugc4_WhatsApp%20Image%202026-03-06%20at%2016.26.44.jpeg';

const OWNER_ROLES = [
  { value: 'club_admin',   label: 'Club Owner',   provisions: 'club' },
  { value: 'venue_admin',  label: 'Venue Owner',  provisions: 'venue' },
  { value: 'school_admin', label: 'School / Academy Owner', provisions: 'club' },
];

export default function OwnerRegister() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('club_admin');
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) {
      toast.error('Please enter your organization name');
      return;
    }
    setLoading(true);
    try {
      await register(name.trim(), email.trim(), password, role);
      // Provision the starter org so the dashboard isn't empty.
      const profile = OWNER_ROLES.find(r => r.value === role);
      try {
        if (profile?.provisions === 'club') {
          await clubsAPI.create({
            name: orgName.trim(),
            description: 'Created during owner signup',
            location: '',
            sport_focus: 'badminton',
          });
        } else if (profile?.provisions === 'venue') {
          await venuesAPI.create({
            name: orgName.trim(),
            address: '',
            courts: 1,
            description: 'Created during owner signup',
            hourly_rate: 0,
            session_rate: 0,
          });
        }
      } catch (err) {
        // Non-fatal — the user can create their org manually from the dashboard.
        console.warn('Starter org provisioning skipped:', err?.response?.data?.detail || err?.message);
      }
      toast.success(`Welcome aboard, ${name.split(' ')[0] || 'owner'}!`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // Persist the chosen owner role + org name so AuthCallback can apply it
    // after the OAuth round-trip. 10-minute TTL — long enough to complete
    // Google's consent flow, short enough that a stale payload doesn't
    // accidentally promote a future player signup to an owner.
    try {
      const payload = {
        role,
        org_name: orgName.trim(),
        name: name.trim(),
        expires_at: Date.now() + 10 * 60 * 1000,
      };
      localStorage.setItem('loveall:pending_owner_signup', JSON.stringify(payload));
    } catch { /* localStorage disabled — fall back to player role */ }
    const redirect = encodeURIComponent(`${window.location.origin}/dashboard`);
    window.location.href = `https://auth.emergentagent.com/?redirect=${redirect}`;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-12 safe-top safe-bottom" data-testid="owner-register-page">
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        data-testid="owner-register-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <img src={LOGO_URL} alt="LoveAll" className="h-16 w-16 rounded-full object-cover mx-auto mb-2" />
          <div className="flex items-center justify-center gap-2 text-primary">
            <Building2 className="h-5 w-5" />
            <CardTitle className="font-heading uppercase tracking-tight">Register Your Club</CardTitle>
          </div>
          <p className="text-xs text-muted-foreground">Set up your club, venue or academy in under a minute</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            type="button"
            variant="outline"
            className="w-full h-11"
            onClick={handleGoogle}
            data-testid="owner-register-google-btn"
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-[10px] uppercase"><span className="bg-card px-2 text-muted-foreground">or with email</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <Label htmlFor="name" className="text-xs">Your Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required data-testid="owner-register-name" />
            </div>
            <div>
              <Label htmlFor="email" className="text-xs">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="owner-register-email" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                required
                data-testid="owner-register-password"
              />
            </div>
            <div>
              <Label htmlFor="role" className="text-xs">I'm running a…</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="owner-register-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OWNER_ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value} data-testid={`owner-register-role-${r.value}`}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="orgName" className="text-xs">Organization Name</Label>
              <Input
                id="orgName"
                placeholder={role === 'venue_admin' ? 'e.g. Indiranagar Sports Hub' : 'e.g. Whitefield Smashers'}
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
                data-testid="owner-register-org-name"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                We'll create a starter {role === 'venue_admin' ? 'venue' : 'club'} so your dashboard is ready instantly.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full h-11 font-bold"
              disabled={loading}
              data-testid="owner-register-submit-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              {loading ? 'Creating your account…' : 'Create Owner Account'}
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground">
            Already an owner?{' '}
            <Link to="/owner-login" className="text-primary underline" data-testid="owner-register-login-link">Sign in</Link>
          </p>
          <p className="text-center text-[11px] text-muted-foreground">
            Just want to play casually? <Link to="/" className="underline" data-testid="owner-register-quickplay-link">Open Quick Play</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
